import { AsyncOperationStatus } from "@sp-crm/core";
import { z } from "zod";

export const sendDidNotFinishMessage = "This did not finish. Please try again.";

const sendResultSchema = z.object({
    status: z.enum(["success", "failure"]),
    accepted: z.array(z.string()),
    rejected: z.array(z.string()),
    code: z.string().nullable(),
    sentMessageId: z.string().nullable(),
});

export interface SendOperationHandle {
    status: AsyncOperationStatus;
    result: unknown;
    errorMessage?: string | null;
}

export type SendOperationOutcome =
    | { kind: "in-progress" }
    | { kind: "sent" }
    | { kind: "partial"; rejected: string[] }
    | {
          kind: "not-delivered";
          rejected: string[];
          code: string | null;
          message: string | null;
      }
    | { kind: "canceled" };

const describeSucceededOperation = (result: unknown): SendOperationOutcome => {
    const parsed = sendResultSchema.safeParse(result);
    if (!parsed.success) {
        return { kind: "not-delivered", rejected: [], code: null, message: null };
    }
    if (parsed.data.status === "failure") {
        return {
            kind: "not-delivered",
            rejected: parsed.data.rejected,
            code: parsed.data.code,
            message: null,
        };
    }
    if (parsed.data.rejected.length > 0) {
        return { kind: "partial", rejected: parsed.data.rejected };
    }
    return { kind: "sent" };
};

export const describeSendOperationOutcome = (
    handle: SendOperationHandle,
): SendOperationOutcome => {
    switch (handle.status) {
        case AsyncOperationStatus.Pending:
        case AsyncOperationStatus.Running:
            return { kind: "in-progress" };
        case AsyncOperationStatus.Succeeded:
            return describeSucceededOperation(handle.result);
        case AsyncOperationStatus.Failed:
            return {
                kind: "not-delivered",
                rejected: [],
                code: null,
                message: handle.errorMessage ?? sendDidNotFinishMessage,
            };
        case AsyncOperationStatus.Canceled:
            return { kind: "canceled" };
        default: {
            const exhaustiveCheck: never = handle.status;
            throw new Error(`Unhandled status: ${exhaustiveCheck}`);
        }
    }
};
